import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  LinearProgress,
  Stack,
  IconButton,
} from "@mui/material";
import VerifiedIcon from "../../Activos/Imagenes/EmailVerification/a.svg";
import { useGlobalState } from "../../Utilidades/useGlobalState";
import { MostrarMensaje } from "../../Utilidades/ManejoMensajes";
import { isValidCodeFormat } from "../../Utilidades/Schemas/VerificationCodeSchema";
import {
  enviarCodigoVerificacion,
  validarCodigoVerificacion,
  cancelarCodigoVerificacion,
} from "../../Actions/Companias/CompaniaAction";

import CloseIcon from "@mui/icons-material/Close";

//styles
import { styleToolBar } from "../Tool/Style";

/**
 * Componente DialogoVerificacionEmail
 * Dialog modal para ingresar código de verificación de 6 dígitos
 *
 * @param {Object} props
 * @param {boolean} props.open - Estado de apertura del dialog
 * @param {Function} props.onClose - Función para cerrar el dialog
 * @param {string} props.email - Email a verificar
 * @param {string} props.companyId - ID de la compañía
 * @param {string} props.customerId - ID del cliente (opcional)
 * @param {string} props.languageCode - Código de idioma (ES, EN, PT)
 * @param {Function} props.onSuccess - Callback cuando verificación es exitosa
 * @param {Function} props.onError - Callback cuando hay error
 */
const DialogoVerificacionEmail = ({
  open = false,
  onClose,
  email,
  companyId,
  customerId = null,
  languageCode = "ES",
  onSuccess,
  onCancel,
  onError,
}) => {
  const { etiquetas, sesionUsuario, dispatch } = useGlobalState();

  // Estados para los 6 inputs de código
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutos en segundos
  const [canResend, setCanResend] = useState(false);
  const [resetTimer, setResetTimer] = useState(0); // Contador para forzar reinicio del timer

  // Referencias para los inputs
  const inputRefs = useRef([]);

  // Timer para el contador
  useEffect(() => {
    if (!open) return;

    // Reiniciar timer cuando se abre el dialog o se reenvía código
    setTimeRemaining(300);
    setCanResend(false);

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, resetTimer]);

  // Auto-focus en primer input al abrir
  useEffect(() => {
    if (open && inputRefs.current[0]) {
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [open]);

  /**
   * Formatea el tiempo restante en MM:SS
   */
  const formatTime = () => {
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  /**
   * Verifica si el código está completo y es válido
   */
  const isCodeComplete = () => {
    const fullCode = code.join("");
    return fullCode.length === 6 && isValidCodeFormat(fullCode);
  };

  /**
   * Maneja el cambio en un input de código
   */
  const handleCodeChange = (index, value) => {
    // Solo permitir números
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus al siguiente input si hay valor
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  /**
   * Maneja el keydown para navegación
   */
  const handleKeyDown = (index, e) => {
    // Backspace: mover al anterior si está vacío
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Arrow keys para navegación
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Enter para verificar si el código está completo
    if (e.key === "Enter" && isCodeComplete()) {
      handleVerifyCode();
    }
  };

  /**
   * Maneja el pegado de código completo
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    // Verificar que sea un código de 6 dígitos
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split("");
      setCode(newCode);
      // Focus en el último input
      inputRefs.current[5]?.focus();
    }
  };

  /*
   * Maneja errores y muestra mensajes correspondientes
   */
  const handleError = (errorCode) => {
    MostrarMensaje(dispatch, "error", [errorCode]);
    onError?.(errorCode);

    // Limpiar inputs de digitos
    setCode(["", "", "", "", "", ""]);
  };

  /**
   * Maneja la verificación del código
   */
  const handleVerifyCode = async () => {
    const fullCode = code.join("");

    if (!isCodeComplete()) {
      MostrarMensaje(dispatch, "warning", ["VerificationCodeInvalid"]);
      return;
    }

    setIsVerifying(true);

    try {
      const result = await validarCodigoVerificacion({
        email,
        code: fullCode,
        companyId,
        userId: sesionUsuario.usuario.userId || null,
      });

      if (result.success && result.isValid) {
        MostrarMensaje(dispatch, "success", ["VerificationCodeSuccess"]);
        onSuccess?.(result.verificationId);
        // Limpiar y cerrar
        setCode(["", "", "", "", "", ""]);
        setTimeRemaining(300);
        setCanResend(false);
        setIsVerifying(false);
      } else {
        // Determinar el tipo de error según el mensaje
        let errorCode = "VerificationCodeInvalid";

        if (
          result.message?.toLowerCase().includes("expirado") ||
          result.message?.toLowerCase().includes("expired")
        ) {
          errorCode = "VerificationCodeExpired";
          setCanResend(true);
          setTimeRemaining(0);
        } else if (
          result.message?.toLowerCase().includes("utilizado") ||
          result.message?.toLowerCase().includes("used")
        ) {
          errorCode = "VerificationCodeAlreadyUsed";
        }

        handleError(errorCode);
      }
    } catch (error) {
      handleError("VerificationCodeError");
    } finally {
      setIsVerifying(false);
    }
  };

  /**
   * Maneja el reenvío de código
   */
  const handleResendCode = async () => {
    setIsVerifying(true);

    try {
      const result = await enviarCodigoVerificacion(
        companyId,
        email,
        customerId,
        languageCode,
      );

      if (result.success) {
        MostrarMensaje(dispatch, "success", ["VerificationCodeSent"]);
        // Reiniciar contador a 5 minutos
        setResetTimer((prev) => prev + 1); // Forzar reinicio del intervalo
        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        MostrarMensaje(dispatch, "error", ["VerificationCodeError"]);
      }
    } catch (error) {
      MostrarMensaje(dispatch, "error", ["VerificationCodeError"]);
    } finally {
      setIsVerifying(false);
    }
  };

  /**
   * Maneja la cancelacion del código
   */
  const handleCancelCode = async () => {
    setIsVerifying(true);

    try {
      const result = await cancelarCodigoVerificacion({
        email,
        companyId,
        userId: sesionUsuario.usuario.userId || null,
      });

      if (result.success) {
        MostrarMensaje(dispatch, "success", ["CancelVerificationCodeSuccess"]);
        onCancel?.(true);
        // Limpiar y cerrar
        setCode(["", "", "", "", "", ""]);
        setTimeRemaining(300);
        setCanResend(false);
        setIsVerifying(false);
      } else {
        MostrarMensaje(dispatch, "error", ["CancelVerificationCodeError"]);
        onCancel?.(false);
        // Limpiar y cerrar
        setCode(["", "", "", "", "", ""]);
        setTimeRemaining(300);
        setCanResend(false);
        setIsVerifying(false);
      }
    } catch (error) {
      MostrarMensaje(dispatch, "error", ["CancelVerificationCodeError"]);
      onCancel?.(false);
      // Limpiar y cerrar
      setCode(["", "", "", "", "", ""]);
      setTimeRemaining(300);
      setCanResend(false);
      setIsVerifying(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        // Prevenir cierre por click en backdrop o ESC
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          padding: "60px",
          width: 328,
          position: "relative",
          overflow: "hidden",
        },
      }}
    >
      {/* Estado de carga en verificación - Posicionado en el borde superior */}
      {isVerifying && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
          }}
        >
          <Stack sx={{ width: "100%", color: "#223354" }} spacing={2}>
            <LinearProgress color="inherit" />
          </Stack>
        </Box>
      )}

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 0,
          gap: "20px",
        }}
      >
        <IconButton
          style={{
            ...styleToolBar.IconButton,
            position: "absolute",
            top: 5,
            right: 5,
          }}
          onClick={handleCancelCode}
          size="large"
        >
          <CloseIcon style={styleToolBar.Icon} />
        </IconButton>
        {/* Icono de escudo */}
        <img
          src={VerifiedIcon}
          alt="Verified"
          style={{ width: 86, height: 98 }}
        />

        {/* Título */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 500,
            color: "#223354",
            fontSize: "24px",
            fontFamily: "'Roboto', sans-serif",
          }}
        >
          {etiquetas?.General?.Et_EmailVerification || "Código de confirmación"}
        </Typography>

        {/* Texto descriptivo */}
        <Typography
          variant="body2"
          sx={{
            color: "#3F425D",
            fontWeight: 400,
            fontSize: "12px",
            lineHeight: "18px",
            fontFamily: "'Roboto', sans-serif",
          }}
        >
          {etiquetas?.General?.Et_EnterVerificationCode ||
            "Por favor, ingresa el código que te enviamos a tu dirección de correo electrónico:"}{" "}
          <strong style={{ color: "#1F2937" }}>{email}</strong>
        </Typography>

        {/* 6 inputs para el código */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: "15px" }}>
          {code.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: "24px",
                  fontWeight: 600,
                  padding: "12px 0",
                  color: "#1F2937",
                  fontFamily: "'Roboto', sans-serif",
                },
              }}
              sx={{
                flex: "1",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  padding: "0px 12px",
                  "& fieldset": {
                    borderColor: "rgba(0,0,0,0.36)",
                    borderWidth: "1px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#9CA3AF",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#082478",
                    borderWidth: "2px",
                  },
                },
              }}
              variant="outlined"
              disabled={isVerifying || timeRemaining === 0}
            />
          ))}
        </Box>

        {/* Texto de validez del código con contador MM:SS */}
        <Typography
          variant="body2"
          sx={{
            color: "#3F425D",
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "18px",
            fontFamily: "'Roboto', sans-serif",
          }}
        >
          {etiquetas?.General?.Et_CodeValidForTimer ||
            "Este código será válido por"}{" "}
          <b>{formatTime()}</b>{" "}
          <b>{etiquetas?.General?.Et_CodeValidMinutes || "minutos"}.</b>
        </Typography>

        {/* Link de reenvío */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <Typography
            variant="body2"
            sx={{
              color: "#3F425D",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "18px",
              fontFamily: "'Roboto', sans-serif",
              display: "inline",
            }}
          >
            {etiquetas?.General?.Et_HaventReceivedCode ||
              "Aún no has recibido el código?"}{" "}
          </Typography>
          {canResend ? (
            <Link
              component="button"
              variant="body2"
              onClick={handleResendCode}
              disabled={isVerifying}
              sx={{
                fontSize: "13px",
                cursor: isVerifying ? "not-allowed" : "pointer",
                color: "#005391",
                textDecoration: "none",
                fontWeight: 400,
                lineHeight: "18px",
                fontFamily: "'Roboto', sans-serif",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {etiquetas?.General?.Et_ResendCode || "Reenviar código"}
            </Link>
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                color: "#9CA3AF",
                display: "inline",
                fontWeight: 400,
                lineHeight: "18px",
                fontFamily: "'Roboto', sans-serif",
              }}
            >
              {etiquetas?.General?.Et_ResendCode || "Reenviar código"}
            </Typography>
          )}
        </Box>
      </DialogContent>

      {/* Botón de verificación */}
      <DialogActions
        sx={{
          justifyContent: "center",
          paddingInline: "24px",
          paddingTop: "20px",
        }}
      >
        <Button
          variant="contained"
          onClick={handleVerifyCode}
          disabled={!isCodeComplete() || isVerifying || timeRemaining === 0}
          sx={{
            width: "100%",
            maxWidth: "300px",
            backgroundColor: "#082478",
            color: "#FFFFFF",
            fontWeight: 600,
            fontSize: "14px",
            textTransform: "none",
            borderRadius: "8px",
            padding: "12px",
            "&:hover": {
              backgroundColor: "#082478",
            },
            "&.Mui-disabled": {
              backgroundColor: "#E5E7EB",
              color: "#9CA3AF",
            },
          }}
        >
          {isVerifying
            ? etiquetas?.General?.Et_Verifying || "Verificando..."
            : etiquetas?.General?.Et_VerifyCode || "Verificar Código"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogoVerificacionEmail;
